import ua from '@tencent/docs-user-agent';
import {
    IBaseViewableServiceLayer,
} from '@src/slide/application/entry/toc/base/service-layer/base-viewable-service-layer.interface';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import {
    isRemotePlayerTest,
} from '@src/slide/infrastructure/module/dependent/runtime-environment/remote-player-test-env';
import CanvasE2EManager from '@src/js/auto-test/canvas-e2e';
import PlayManager from '@src/slide/application/service/play-service/play-manager';
import { IUserPriviledgeService } from '@src/slide/application/service/user-priviledge-service/user-priviledge-service.interface';
import UserPriviledgeService from '@src/slide/application/service/user-priviledge-service';
import TipsManager from '@src/slide/application/service/tips-manager/core';
import EditingStateManager from '@src/slide/application/service/editing-state-manager';
import AddonManager from '@src/slide/application/service/addon/addon-manager';
import { SelectionEventService } from '@src/slide/application/service/selection/event-service';
import EditingPresentManager from '@src/slide/application/service/editing-present-manager/core';
import ObserverFactory from '@src/slide/application/service/modelObserver/observerFactory';
import { IAccessibilityManager } from '@src/slide/application/service/accessibility-manager/core/accessibility-manager.interface';
import { DynamicServiceLoader, IDynamicService } from '@src/slide/infrastructure/module/independent/dynamic-loader/dynamic-service';
import { accessibilityManagerDynamicConfig } from '@src/slide/application/entry/toc/editor/pc/service-layer/service-dynamic-configs/accessibility-manager.config';
import { BlankPageService } from '@src/slide/application/service/blank-page-service/core';
import { IAccusationService } from '@src/slide/application/service/accusation-service/core/accusation-service.interface';
import { accusationServiceDynamicConfig } from '@src/slide/application/entry/toc/editor/pc/service-layer/service-dynamic-configs/accusation-service.config';
import TaskMonitor from '@src/slide/infrastructure/module/dependent/taskManager/taskMonitor';
import {
    INotifyManager,
    INotifyManagerInjectable,
} from '@src/slide/application/service/notify-manager/core/notify-manager.interface';
import { notifyManagerDynamicConfig } from '@src/slide/application/entry/toc/base/service-layer/service-dynamic-configs/notify-manager.config';
import LinkManager from '@src/slide/application/service/link-manager/core';
import { ILinkManager } from '@src/slide/application/service/link-manager/core/link-manager.interface';
import { LinkManagerInjectable } from '@src/slide/application/service/link-manager/inject/link-manager-injectable';
import { ToolbarStateManager } from '@src/slide/application/service/toolbar-state-manager/core';
import { ILiveManager } from '@src/slide/application/service/live-manager/core/interface';
import { liveManagerDynamicConfig } from '@src/slide/application/entry/toc/base/service-layer/service-dynamic-configs/live-manager.config';
import { IWatermarkManager } from '@src/slide/application/service/watermark-manager/core/interface';
import { watermarkManagerDynamicConfig } from '@src/slide/application/entry/toc/base/service-layer/service-dynamic-configs/watermark-manager.config';
import { isReleaseBuild, isTestEnvId } from 'utils';
import { ImgLoaderManager } from '@src/slide/application/service/img-loader-manager/core';
import { IRightSideBarService } from '@src/slide/application/service/right-side-bar-service/core/interface';
import RightSideBarService from '@src/slide/application/service/right-side-bar-service/core';
import { TipsManagerInjectable } from '@src/slide/application/service/tips-manager/inject/toc/tips-manager-injectable';
import ENV from '@src/slide/infrastructure/module/dependent/runtime-environment/enviroment';
import { ITipsManagerInjectable } from '@src/slide/application/service/tips-manager/core/tips-manager.interface';
import { CoreViewableServiceLayer } from '@src/slide/content-viewer/entry/view-layer/core-viewable-service-layer';
import { IInfrastructureBase } from '@src/slide/infrastructure/entry/base/infrastructure-base.interface';
import MediaManager from '@src/slide/application/service/media-manager';
import { IMediaManager } from '@src/slide/application/service/media-manager/media-manager.interface';
import AttachmentManager from '@src/slide/application/service/attachment-upload/attachment-manager';
import { IAttachmentManager } from '@src/slide/application/service/attachment-upload/attachment-manager.interface';
import { IBaseExtensionService } from '@src/slide/application/service/edit-extension/core/base/interface';
import BaseExtensionService from '@src/slide/application/service/edit-extension/core/base';
import { IEditorLifeCycleService } from '@src/slide/application/service/editor-lifecycle/core/interface';
import EditorLifeCycleService from '@src/slide/application/service/editor-lifecycle/core';
import { NotifyManagerInjectable } from '@src/slide/application/service/notify-manager/inject/toc';
import { GifService } from '@src/slide/application/service/gif-service';

/**
 * 各个web制品都需要依赖的查看层
 */
export abstract class BaseViewableServiceLayer extends CoreViewableServiceLayer implements IBaseViewableServiceLayer {
    public readonly selectionEventService: SelectionEventService;
    public readonly playManager: PlayManager;
    public readonly userPriviledgeService: IUserPriviledgeService;
    public readonly tipsManager: TipsManager;
    public readonly mediaManager: IMediaManager;
    public readonly editingStateManager: EditingStateManager;
    public readonly addonManager: AddonManager;
    public readonly editingPresentManager?: EditingPresentManager;
    public readonly accessibilityManager: IDynamicService<IAccessibilityManager>;
    public readonly blankPageService: BlankPageService;
    public readonly accusationService: IDynamicService<IAccusationService>;
    public readonly notifyManager: IDynamicService<INotifyManager>;
    public readonly linkManager: ILinkManager;
    public readonly toolbarStateManager: ToolbarStateManager;
    public readonly liveManager: IDynamicService<ILiveManager>;
    public readonly watermarkManager: IDynamicService<IWatermarkManager>;
    public readonly imgLoaderManager: ImgLoaderManager;
    public readonly rightSideBarService: IRightSideBarService;
    public readonly attachmentManager: IAttachmentManager;
    public readonly extensionService: IBaseExtensionService;
    public readonly lifeCycleService: IEditorLifeCycleService;
    public readonly gifService: GifService;

    protected tasks: { run(): void }[];

    public constructor(
        uiDataService: IUiDataService,
        infrastructure: IInfrastructureBase,
    ) {
        super(uiDataService, infrastructure);
        this.lifeCycleService = new EditorLifeCycleService();
        this.attachmentManager = new AttachmentManager({
            uiDataService,
            selectionService: this.selectionService,
            slideRenderStateManager: this.slideRenderStateManager,
        });
        TaskMonitor.getInstance().startObserveLongtask();
        uiDataService.setSelectionDelegate(this.selectionService);
        this.editingStateManager = new EditingStateManager();
        ObserverFactory.setEditingStateManagerDelegate(this.editingStateManager);
        this.mediaManager = new MediaManager({ attachmentManager: this.attachmentManager });
        ObserverFactory.setMediaManagerDelegate(this.mediaManager);
        if (isRemotePlayerTest() || __IS_DEV__ || ENV.IS_TEST) {
            CanvasE2EManager.getInstance().setServiceDelegate({
                rendererService: this.contentRendererService,
                selection: this.selectionService,
            });
        }
        this.playManager = new PlayManager(
            this.selectionService,
            this.editorInstanceService,
        );
        this.tipsManager = new TipsManager({
            selectionService: this.selectionService,
        }, this.createTipManagerInject());
        this.userPriviledgeService = new UserPriviledgeService(this.selectionService, uiDataService);
        this.initUserPriviledgeService();
        this.selectionEventService = new SelectionEventService();
        this.selectionEventService.setSelectionServiceDelegate({
            selectionService: this.selectionService,
            privilegeService: this.userPriviledgeService,
        });
        this.addonManager = new AddonManager(this.slideRenderStateManager, this.selectionService);
        this.addonManager.setSelectionService(this.selectionService);
        this.addonManager.setUiDataService(uiDataService);
        this.blankPageService = new BlankPageService();

        // [腾讯会议场景] 开启编辑态跟随管理
        if (ua.isTencentMeeting) this.editingPresentManager = new EditingPresentManager(this.selectionService);
        this.accessibilityManager = DynamicServiceLoader.getInstance()
            .registerDynamicService(accessibilityManagerDynamicConfig);
        this.accessibilityManager.onDidInit((a11yManager) => {
            a11yManager.setServiceDelegates({
                selectionService: this.selectionService,
                uiDataService,
                addonManager: this.addonManager,
            });
        });
        this.accusationService = DynamicServiceLoader.getInstance()
            .registerDynamicService(accusationServiceDynamicConfig);
        this.accusationService.onDidInit((accusationService) => {
            accusationService.setServiceDelegates({
                extensionService: this.extensionService,
            });
        });
        this.notifyManager = DynamicServiceLoader.getInstance()
            .registerDynamicService(notifyManagerDynamicConfig);
        this.notifyManager.onDidInit((notifyManager) => {
            notifyManager.setServiceDelegates({
                rightSideBarService: this.rightSideBarService,
            });
            notifyManager.registerRightSideBar();
            notifyManager.setInject(this.createNotifyManagerInjectable());
        });
        this.linkManager = new LinkManager(
            uiDataService,
            this.selectionService,
            this.addonManager,
            this.playManager,
            this.editingStateManager,
            this.editorInstanceService,
            this.userPriviledgeService,
            new LinkManagerInjectable(),
        );
        this.addonManager.setLinkManager(this.linkManager);
        this.toolbarStateManager = new ToolbarStateManager();
        this.liveManager = DynamicServiceLoader.getInstance().registerDynamicService(liveManagerDynamicConfig);
        this.watermarkManager = DynamicServiceLoader.getInstance()
            .registerDynamicService(watermarkManagerDynamicConfig);
        this.watermarkManager.onDidInit((watermarkManager) => {
            if (isTestEnvId() || !isReleaseBuild()) {
                // 给测试环境挂接口，之前是在getInstance的时候做的
                // @ts-ignore
                window.watermarkManager = watermarkManager;
            }
        });
        this.imgLoaderManager = new ImgLoaderManager();
        this.rightSideBarService = new RightSideBarService();

        this.extensionService = this.createExtensionService();
        this.extensionService.setServiceDelegates({
            lifeCycleService: this.lifeCycleService,
        });
        this.gifService = new GifService();
        this.gifService.setDelegate({
            selectionService: this.selectionService,
            playManager: this.playManager,
        });
    }

    protected createExtensionService(): IBaseExtensionService {
        return new BaseExtensionService();
    }

    protected createTipManagerInject(): ITipsManagerInjectable {
        return new TipsManagerInjectable();
    }

    protected createNotifyManagerInjectable(): INotifyManagerInjectable {
        return new NotifyManagerInjectable();
    }

    private initUserPriviledgeService(): void {
        this.tipsManager.setUserPriviledgeServiceDelegate(this.userPriviledgeService);
    }
}
